import { deleteSession } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getRequiresAuthorization } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

const activityThrottleMs = 30_000;
const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"] as const;
const localStorageKey = "isc_lastActivity";

const SessionTimeoutWatcher = () => {
    const isAuthenticated = useSelector((state: ApplicationState) => state.context.session.isAuthenticated);
    const isGuest = useSelector((state: ApplicationState) => state.context.session.isGuest);
    const rememberMe = useSelector((state: ApplicationState) => state.context.session.rememberMe);
    const keepMeSignedIn = useSelector((state: ApplicationState) => state.context.session.keepMeSignedIn);
    const siteTimeoutMinutes = useSelector(
        (state: ApplicationState) => getSettingsCollection(state).accountSettings.siteTimeoutMinutes,
    );
    const currentPageRequiresAuth = useSelector((state: ApplicationState) =>
        getRequiresAuthorization(state, getCurrentPage(state).id),
    );
    const signInPageUrl = useSelector((state: ApplicationState) => getPageLinkByPageType(state, "SignInPage")?.url);

    const isActive = isAuthenticated && !isGuest;

    const siteTimeoutMinutesRef = useRef(siteTimeoutMinutes);
    siteTimeoutMinutesRef.current = siteTimeoutMinutes;
    const currentPageRequiresAuthRef = useRef(currentPageRequiresAuth);
    currentPageRequiresAuthRef.current = currentPageRequiresAuth;
    const signInPageUrlRef = useRef(signInPageUrl);
    signInPageUrlRef.current = signInPageUrl;

    useEffect(() => {
        if (!isActive || rememberMe || keepMeSignedIn) {
            return;
        }

        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let lastActivityReset = 0;

        const resetTimer = () => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
            const timeoutMs = siteTimeoutMinutesRef.current * 60_000;
            if (timeoutMs > 0) {
                timeoutId = setTimeout(handleTimeout, timeoutMs);
            }
        };

        const handleActivity = () => {
            const now = Date.now();
            if (now - lastActivityReset < activityThrottleMs) {
                return;
            }
            lastActivityReset = now;
            try {
                localStorage.setItem(localStorageKey, now.toString());
            } catch {
                // localStorage unavailable (private browsing, full storage, etc.)
            }
            resetTimer();
        };

        const handleTimeout = async () => {
            timeoutId = undefined;

            const timeoutMs = siteTimeoutMinutesRef.current * 60_000;
            try {
                const raw = localStorage.getItem(localStorageKey);
                if (raw) {
                    const remainingMs = Number(raw) + timeoutMs - Date.now();
                    if (remainingMs > 0) {
                        timeoutId = setTimeout(handleTimeout, remainingMs);
                        return;
                    }
                }
            } catch {
                // localStorage unavailable — proceed with logout
            }

            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
                timeoutId = undefined;
            }
            for (const event of activityEvents) {
                window.removeEventListener(event, handleActivity);
            }

            try {
                localStorage.removeItem(localStorageKey);
            } catch {
                // localStorage unavailable
            }

            await deleteSession();

            if (currentPageRequiresAuthRef.current && signInPageUrlRef.current) {
                window.location.href = signInPageUrlRef.current;
            } else {
                window.location.reload();
            }
        };

        resetTimer();
        for (const event of activityEvents) {
            window.addEventListener(event, handleActivity, { passive: true });
        }

        return () => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
            for (const event of activityEvents) {
                window.removeEventListener(event, handleActivity);
            }
        };
    }, [isActive, rememberMe, keepMeSignedIn]);

    return null;
};

export default SessionTimeoutWatcher;
